import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Stack, Button, Grid, Box, IconButton } from '@mui/material';
import { Icon } from "@iconify/react";
import { Controller, useForm } from 'react-hook-form';
import { ACTIONS } from '../../config/const.config';
import {
  LISTING_MOVIES_COLUMNS,
  LISTING_MOVIES_ACTIONS,
} from '../../config/module-configs/manage-content.config';
import ListData from '../table-elements/list-data.component';
import Iconify from '../common/iconify.component';
import ConfirmPopup from '../common/confirm-popup.component';
import SnackbarInfo from '../common/snackbar-info.component';
import validationRules from '../../utils/validations.util';
import {
  assignMovie,
  viewMoviesById,
  deleteAssignedMovieById,
} from '../../services/categories.service';
import SearchableContent from '../form-elements/searchableContent.component';
import ROUTE_PATH from '../../config/routes.config';
import editUrl from '../../utils/url-edit.util';

const ManageCategoryContent = ({ id }) => {
  const moviesColumns = LISTING_MOVIES_COLUMNS;
  const moviesActions = LISTING_MOVIES_ACTIONS;
  const navigate = useNavigate();
  const [formSubmitted, setFormSubmitted] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      categories: [],
    },
  });
  const [moviesOptions, setMoviesOptions] = useState({
    page: 0,
    rowsPerPage: 10,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    filtersCounter: 0,
    categoryName: '',
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const handleMoviePageChange = (v) => {
    setMoviesOptions({
      ...moviesOptions,
      page: v,
      reloadCounter: moviesOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleMovieRowsPerPageChange = (v) => {
    setMoviesOptions({
      ...moviesOptions,
      rowsPerPage: v,
      page: 0,
      reloadCounter: moviesOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleMovieSortingChange = (sortBy, sortOrder) => {
    if (moviesOptions.loading) return;
    setMoviesOptions({
      ...moviesOptions,
      sortBy,
      sortOrder,
      reloadCounter: moviesOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleMovieRefreshData = () => {
    setMoviesOptions({
      ...moviesOptions,
      reloadCounter: moviesOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleActionSelect = (selectedAction, selectedData) => {
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };
  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };
  const handleActionSuccess = (message = '') => {
    if (message) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'success',
        message,
      });
    }

    handleActionCancel();
    handleMovieRefreshData();
  };

  const handleDeleteMovieData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteAssignedMovieById(id, doAction.data.id)
      .then(() => {
        handleActionSuccess('Assigned movie deleted successfully.');
        handleMovieRefreshData();
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  const onMoviesFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    const selectedMovies = [];
    for (let i = 0; i < data.movies.length; i += 1) {
      selectedMovies.push(data.movies[i].id);
    }
    const payload = {
      movies: selectedMovies,
    };
    assignMovie(id, payload)
      .then((res) => {
        if (res.data) {
          handleMovieRefreshData();
          handleActionSuccess(' Movie assigned successfully.');
          setValue('movies', []);
        }
      })
      .catch((e) => e);

    setFormSubmitted(false);
  };

  useEffect(() => {
    const params = [];
    params.push(`page=${moviesOptions.page + 1}`);
    params.push(`perPage=${moviesOptions.rowsPerPage}`);
    if (moviesOptions.sortBy && moviesOptions.sortOrder) {
      params.push(`sortBy=${moviesOptions.sortBy}`);
      params.push(`sortOrder=${moviesOptions.sortOrder}`);
    }

    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    viewMoviesById(id, paramsQuery)
      .then((res) => {
        setMoviesOptions({
          ...moviesOptions,
          loading: false,
          totalRows: res?.data?.totalRows || 0,
          rows: res?.data?.rows || [],
          categoryName: res?.data?.categoryName || '',
          error: false,
        });
      })
      .catch(() => {
        setMoviesOptions({
          ...moviesOptions,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          categoryName: '',
          error: true,
        });
      });
  }, [moviesOptions.reloadCounter]);

  const handleOpenPage = () => {
    const url = editUrl(ROUTE_PATH.CATEGORY_VIDEOS_ORDER, id);
    navigate(url);
  };

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 1, py: 1 }}
      >
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 1, py: 1 }}
          >
	        <Link to={ROUTE_PATH.CONTENT_CATEGORIES}>
							<IconButton color="primary" variant="contained" >
                <Icon icon="material-symbols:arrow-back-ios-new" />
              </IconButton>
	          </Link>
	            <Box sx={{marginLeft:0.2}} fontWeight="bold" fontSize="20px">{`Manage Videos ${
	              moviesOptions.categoryName ? `- ${moviesOptions.categoryName}` : ''
	            }`}</Box> 
        </Stack>
        
        <Button
          color="inherit"
          variant="contained"
          onClick={() => handleOpenPage()}
          disabled={
            formSubmitted ||
            (moviesOptions.totalRows === 0)
          }
        >
          Manage Videos Order
        </Button>        
      </Stack>

        
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 1, py: 1 }}
      >
        <Box
          width="50%"
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Box width="90%">
            <form
              id="add-edit-movie-form"
              onSubmit={handleSubmit(onMoviesFormSubmit)}
            >
              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="movies"
                  control={control}
                  rules={validationRules.REQUIRED}
                  defaultValue={[]}
                  render={({ field: { onChange, value } }) => (
                    <SearchableContent
                      id="movies"
                      name="movies"
                      label="Videos"
                      defaultValue={value}
                      multiple
                      onChange={onChange}
                      sx={{ width: '100%', my: 1 }}
                      error={errors?.movies?.message || ''}
                    />
                  )}
                />
              </Grid>
            </form>
          </Box>

          <Button
            type="submit"
            color="primary"
            form="add-edit-movie-form"
            variant="contained"
            startIcon={<Iconify icon="ic:round-add" />}
            disabled={formSubmitted}
            sx={{ marginLeft: 2,
              '&:hover': {
              color: 'white', // text color on hover
              },  
            }}
          >
            Assign
          </Button>
        </Box>

        <Box
          width="40%"
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
        >
          <Button
            color="inherit"
            variant="contained"
            startIcon={<Iconify icon="ic:twotone-refresh" />}
            onClick={handleMovieRefreshData}
            disabled={moviesOptions.loading}
          >
            Refresh
          </Button>
        </Box>
      </Stack>

      <Card>
        <ListData
          columns={moviesColumns}
          rows={moviesOptions.rows}
          page={moviesOptions.page}
          rowsPerPage={moviesOptions.rowsPerPage}
          totalRows={moviesOptions.totalRows}
          loading={moviesOptions.loading}
          actions={moviesActions}
          error={moviesOptions.error}
          sortBy={moviesOptions.sortBy}
          sortOrder={moviesOptions.sortOrder}
          onPageChange={handleMoviePageChange}
          onRowsPerPageChange={handleMovieRowsPerPageChange}
          onSortChange={handleMovieSortingChange}
          onAction={handleActionSelect}
        />
      </Card>

      {doAction.action === ACTIONS.DELETE.value && (
        <ConfirmPopup
          title="Delete Assigned Movies"
          message="Do you want to delete this Movie? You can not undo this action!"
          noteText=""
          onCancel={handleActionCancel}
          onSuccess={handleDeleteMovieData}
        />
      )}
        

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

ManageCategoryContent.propTypes = {
  id: PropTypes.string.isRequired,
};

export default ManageCategoryContent;
